import {
    Button,
    Checkbox,
    Flex,
    HStack,
    Icon,
    IconButton,
    Input,
    Tag,
    TagCloseButton,
    TagLabel,
    Text,
    Tr,
    useDisclosure,
    useToast,
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { FaPlus } from 'react-icons/fa';
import { TbBriefcase, TbMapPin, TbShieldLock, TbTrash, TbUpload, TbUserCheck } from 'react-icons/tb';
import type { LocationEntry } from '../../../../../hooks/useGetLocations';
import type { Role } from '../../../../../hooks/useValidateSession';
import type { InvitePositionOption } from '../../../../../utils/invitePositionOptions';
import {
    INVITE_TEMPLATE_ACCEPT,
    downloadInviteTemplate,
    isSupportedInviteFile,
    parseInviteWorkbook,
} from '../../../../../utils/inviteUsersTemplate';
import { ImportUploadModal } from '../../../../../components/import/ImportUploadModal';
import BulkActionBar, { type BulkActionBarItem } from '../../../../../components/BulkActionBar';
import UsersBulkEditModal, {
    type UsersBulkField,
    type UsersBulkValue,
} from '../../../../../components/settings/UsersBulkEditModal';
import {
    InlineLocationsCell,
    InlineSelectCell,
} from '../../../../../components/settings/UsersInlineCells';
import {
    EDITABLE_CELL_ACTIVE,
    EDITABLE_CELL_GEOMETRY,
    EDITABLE_CELL_HOVER,
    PARTBAY_BLUE,
    SETTINGS_TABLE_GUTTER,
    SettingsTable,
    SettingsTableFooter,
    SettingsTd,
    SettingsTh,
} from '../../../../../components/settings/primitives';
import {
    INVITE_ROLE_OPTIONS,
    invitePermissionsLabel,
    isValidInviteEmail,
    newInviteDraft,
    type InviteDraft,
    type InviteRoleValue,
} from './inviteDraft';

export interface AddUsersTableStepProps {
    rows: InviteDraft[];
    onChange: (rows: InviteDraft[]) => void;
    positionOptions: InvitePositionOption[];
    /** Invoicing-only orgs have exactly one position, so the column is locked. */
    isPositionLocked?: boolean;
    locations: LocationEntry[];
    /** Opens the flow's custom-permissions view for these rows. */
    onEditPermissions: (rowIds: string[]) => void;
}

/** Bulk bar actions; the first three open the shared value picker. */
const BULK_ACTIONS: BulkActionBarItem[] = [
    { id: 'position', label: 'Position', icon: TbBriefcase as React.ElementType },
    { id: 'locations', label: 'Locations', icon: TbMapPin as React.ElementType },
    { id: 'role', label: 'Role', icon: TbUserCheck as React.ElementType },
    { id: 'permissions', label: 'Permissions', icon: TbShieldLock as React.ElementType },
];

/**
 * Details step for adding several users at once: one row per invite in the same
 * full-bleed table the users list uses, so the fields are edited in place and
 * the whole batch is visible while it is built. Rows can be filled by hand or
 * imported from the invite spreadsheet, and a selection can be given the same
 * position, locations, role, or permissions in one go.
 */
const AddUsersTableStep = ({
    rows,
    onChange,
    positionOptions,
    isPositionLocked = false,
    locations,
    onEditPermissions,
}: AddUsersTableStepProps) => {
    const toast = useToast();
    const uploadModal = useDisclosure();
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [bulkField, setBulkField] = useState<UsersBulkField | null>(null);
    const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);

    const selectedRows = useMemo(
        () => rows.filter((row) => selectedIds.has(row.id)),
        [rows, selectedIds],
    );
    const allSelected = rows.length > 0 && rows.every((row) => selectedIds.has(row.id));
    const someSelected = rows.some((row) => selectedIds.has(row.id));
    const completeCount = rows.filter((row) => isValidInviteEmail(row.email) && row.position).length;

    const updateRow = (id: string, changes: Partial<InviteDraft>) => {
        onChange(rows.map((row) => (row.id === id ? { ...row, ...changes } : row)));
    };

    const addRow = () => {
        onChange([
            ...rows,
            newInviteDraft({ position: isPositionLocked ? ('invoicing' as Role) : '' }),
        ]);
    };

    const removeRow = (id: string) => {
        onChange(rows.filter((row) => row.id !== id));
        setSelectedIds((prev) => {
            if (!prev.has(id)) return prev;
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
    };

    const toggleSelect = (id: string, checked: boolean) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (checked) next.add(id);
            else next.delete(id);
            return next;
        });
    };

    const toggleSelectAll = (checked: boolean) => {
        setSelectedIds(checked ? new Set(rows.map((row) => row.id)) : new Set());
    };

    const handleBulkAction = (actionId: string) => {
        // Permissions is a whole matrix, so it opens the flow's own view rather
        // than the one-value picker the other fields share.
        if (actionId === 'permissions') {
            onEditPermissions(selectedRows.map((row) => row.id));
            return;
        }
        setBulkField(actionId as UsersBulkField);
    };

    const handleBulkApply = (value: UsersBulkValue) => {
        onChange(
            rows.map((row) => {
                if (!selectedIds.has(row.id)) return row;
                const next = { ...row };
                if (value.position !== undefined) {
                    // The custom set was seeded from the old position's
                    // defaults, so it resets with the position, as in the
                    // single-user form.
                    next.position = value.position;
                    next.customPermissions = null;
                }
                if (value.locationId !== undefined) next.locationId = value.locationId;
                if (value.admin !== undefined) {
                    next.role = value.admin ? 'admin' : 'member';
                    if (value.admin) next.customPermissions = null;
                }
                return next;
            }),
        );
        setBulkField(null);
    };

    const handleDownloadTemplate = async () => {
        await downloadInviteTemplate(locations, positionOptions);
    };

    const handleUploadFile = async (file: File) => {
        try {
            const { rows: parsed, skippedSheets } = await parseInviteWorkbook(file, {
                locations,
                positionOptions,
                isInvoicingOrg: isPositionLocked,
            });

            if (parsed.length === 0) {
                toast({
                    title: 'No valid rows',
                    description: 'No rows with matching locations and emails were found.',
                    status: 'error',
                    duration: 4000,
                    isClosable: true,
                });
                return;
            }

            // The upload replaces the table, matching the old bulk invite modal.
            onChange(parsed.map((row) => newInviteDraft(row)));
            setSelectedIds(new Set());
            setUploadedFileName(file.name);
            toast({
                title: `${parsed.length} user${parsed.length === 1 ? '' : 's'} imported`,
                description:
                    skippedSheets > 0
                        ? `${skippedSheets} sheet(s) with unmatched locations were skipped.`
                        : 'Review the rows below before saving.',
                status: skippedSheets > 0 ? 'warning' : 'success',
                duration: 4000,
                isClosable: true,
            });
        } catch {
            toast({
                title: 'Failed to read file',
                description: 'The file could not be parsed.',
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        }
    };

    return (
        <>
            <Flex
                justify="space-between"
                align="center"
                gap={4}
                px={SETTINGS_TABLE_GUTTER}
                pt={5}
                pb={4}
                flexShrink={0}
            >
                <HStack spacing={3} minW={0}>
                    <Text fontSize="sm" color="gray.600">
                        {rows.length} row{rows.length === 1 ? '' : 's'} · {completeCount} ready to
                        invite
                    </Text>
                    {uploadedFileName && (
                        <Tag size="sm" colorScheme="green" borderRadius="full" variant="subtle">
                            <TagLabel fontSize="xs">{uploadedFileName}</TagLabel>
                            <TagCloseButton onClick={() => setUploadedFileName(null)} />
                        </Tag>
                    )}
                </HStack>
                <HStack spacing={2} flexShrink={0}>
                    <Button
                        size="sm"
                        variant="outlineGray"
                        leftIcon={<Icon as={TbUpload as React.ElementType} boxSize={4} />}
                        onClick={uploadModal.onOpen}
                    >
                        Upload spreadsheet
                    </Button>
                    <Button
                        size="sm"
                        variant="outlineGray"
                        leftIcon={<Icon as={FaPlus as React.ElementType} boxSize={3} />}
                        onClick={addRow}
                    >
                        Add user
                    </Button>
                </HStack>
            </Flex>

            <SettingsTable
                head={
                    <>
                        <SettingsTh w="44px">
                            <Checkbox
                                size="sm"
                                colorScheme="partbay-blue"
                                isChecked={allSelected}
                                isIndeterminate={!allSelected && someSelected}
                                isDisabled={rows.length === 0}
                                onChange={(e) => toggleSelectAll(e.target.checked)}
                                aria-label="Select all rows"
                            />
                        </SettingsTh>
                        <SettingsTh minW="260px">EMAIL</SettingsTh>
                        <SettingsTh minW="170px">POSITION</SettingsTh>
                        <SettingsTh minW="200px">LOCATIONS</SettingsTh>
                        <SettingsTh minW="130px">ROLE</SettingsTh>
                        <SettingsTh minW="140px">PERMISSIONS</SettingsTh>
                        <SettingsTh w="60px" textAlign="center">
                            ACTIONS
                        </SettingsTh>
                    </>
                }
            >
                {rows.map((row) => {
                    const showEmailError =
                        row.email.trim().length > 0 && !isValidInviteEmail(row.email);
                    return (
                        // role="group" powers the chevron that fades in on hover
                        // for every editable cell in the row.
                        <Tr key={row.id} role="group" _hover={{ bg: 'gray.25' }}>
                            <SettingsTd>
                                <Checkbox
                                    size="sm"
                                    colorScheme="partbay-blue"
                                    isChecked={selectedIds.has(row.id)}
                                    onChange={(e) => toggleSelect(row.id, e.target.checked)}
                                    aria-label={`Select ${row.email || 'new row'}`}
                                />
                            </SettingsTd>
                            <SettingsTd>
                                <Input
                                    variant="unstyled"
                                    fontSize="sm"
                                    type="email"
                                    placeholder="user@example.com"
                                    value={row.email}
                                    onChange={(e) => updateRow(row.id, { email: e.target.value })}
                                    aria-label="Email"
                                    {...EDITABLE_CELL_GEOMETRY}
                                    {...EDITABLE_CELL_HOVER}
                                    borderColor={showEmailError ? 'red.300' : 'transparent'}
                                    _focusVisible={EDITABLE_CELL_ACTIVE}
                                />
                            </SettingsTd>
                            <SettingsTd>
                                <InlineSelectCell
                                    value={row.position}
                                    options={positionOptions}
                                    onSelect={(next) =>
                                        // Changing position rebases permissions,
                                        // so any custom set is dropped with it.
                                        updateRow(row.id, {
                                            position: next as Role,
                                            customPermissions: null,
                                        })
                                    }
                                    isDisabled={isPositionLocked}
                                    ariaLabel="Edit position"
                                    placeholder="Select position"
                                    searchPlaceholder="Search positions"
                                />
                            </SettingsTd>
                            <SettingsTd>
                                <InlineLocationsCell
                                    value={row.locationId}
                                    locations={locations}
                                    onSave={(locationId) => updateRow(row.id, { locationId })}
                                />
                            </SettingsTd>
                            <SettingsTd>
                                <InlineSelectCell
                                    value={row.role}
                                    options={INVITE_ROLE_OPTIONS}
                                    onSelect={(next) =>
                                        updateRow(row.id, {
                                            role: next as InviteRoleValue,
                                            customPermissions:
                                                next === 'admin' ? null : row.customPermissions,
                                        })
                                    }
                                    ariaLabel="Edit role"
                                    minW="160px"
                                />
                            </SettingsTd>
                            <SettingsTd>
                                {row.role === 'admin' ? (
                                    <Text fontSize="sm" color="gray.500">
                                        Admin
                                    </Text>
                                ) : (
                                    // The matrix is a screen of its own, so the
                                    // cell links into it rather than editing here.
                                    <Text
                                        as="button"
                                        type="button"
                                        fontSize="sm"
                                        textAlign="left"
                                        color={row.position ? 'gray.700' : 'gray.400'}
                                        _hover={
                                            row.position
                                                ? { color: PARTBAY_BLUE, textDecoration: 'underline' }
                                                : undefined
                                        }
                                        onClick={() => row.position && onEditPermissions([row.id])}
                                    >
                                        {row.position ? invitePermissionsLabel(row) : '—'}
                                    </Text>
                                )}
                            </SettingsTd>
                            <SettingsTd textAlign="center">
                                <IconButton
                                    aria-label="Remove row"
                                    icon={<Icon as={TbTrash as React.ElementType} boxSize={4} />}
                                    size="xs"
                                    variant="ghost"
                                    colorScheme="red"
                                    isDisabled={rows.length <= 1}
                                    onClick={() => removeRow(row.id)}
                                />
                            </SettingsTd>
                        </Tr>
                    );
                })}
            </SettingsTable>

            <SettingsTableFooter>
                <Text fontSize="xs" color="gray.500">
                    Invitation emails go out when you save. Rows without an email, position, and
                    location are skipped.
                </Text>
            </SettingsTableFooter>

            <BulkActionBar
                selectedCount={selectedRows.length}
                actions={BULK_ACTIONS}
                onClearSelection={() => setSelectedIds(new Set())}
                onAction={handleBulkAction}
            />

            <UsersBulkEditModal
                field={bulkField}
                selectedCount={selectedRows.length}
                positionOptions={positionOptions}
                locations={locations}
                isSaving={false}
                onApply={handleBulkApply}
                onClose={() => setBulkField(null)}
            />

            <ImportUploadModal
                isOpen={uploadModal.isOpen}
                onClose={uploadModal.onClose}
                onFileSelected={(file) => {
                    void handleUploadFile(file);
                    uploadModal.onClose();
                }}
                onDownloadTemplate={() => void handleDownloadTemplate()}
                title="Upload spreadsheet"
                accept={INVITE_TEMPLATE_ACCEPT}
                supportedFormatsLabel="CSV, XLS, XLSX"
                maxSizeMB={25}
                isSupportedFile={isSupportedInviteFile}
            />
        </>
    );
};

export default AddUsersTableStep;
